import type { Narrative } from '../repository'

/**
 * Narrative map keyed by exampleId.
 * 15 entries covering all 5 entry points × 3 candidates each.
 */
export const narrativeMap: Record<string, Narrative> = {
  // block-editor entry (card-product variants)
  'cexample:card-product-minimal': {
    headline: 'Minimal product card · clean layout',
    body: 'A stripped-back product card optimised for density and scannability. Works well in tight grid layouts where visual noise must be minimised.',
    useCases: ['E-commerce category pages', 'Search results listings', 'Quick-add product rails'],
  },
  'cexample:card-product-with-badge': {
    headline: 'Product card with highlight ring',
    body: 'The primary ring draws the eye to featured or promoted products within a grid. Pair it with a "Featured" label to reinforce the emphasis.',
    useCases: ['Homepage featured collections', 'Sale or clearance sections', 'Editor\'s-pick highlights'],
  },
  'cexample:card-product-horizontal': {
    headline: 'Horizontal product card · list layout',
    body: 'Rotates the card into a landscape orientation, freeing space for more descriptive copy alongside the product image. Ideal for comparison-heavy contexts.',
    useCases: ['Comparison pages', 'Cart or wish-list summaries', 'Mobile-first detail views'],
  },

  // chat entry (cta-banner variants)
  'cexample:cta-banner-default': {
    headline: 'CTA banner · standard call-to-action strip',
    body: 'A full-width strip that anchors a single conversion action. The constrained layout focuses attention without overwhelming surrounding content.',
    useCases: ['Newsletter sign-up prompts', 'Trial or demo CTAs', 'Upgrade nudges inside dashboards'],
  },
  'cexample:cta-banner-centered': {
    headline: 'Centered CTA banner · hero-style emphasis',
    body: 'Centres all elements for a symmetrical, hero-like presence. Best used as a standalone section between content blocks to create a natural pause.',
    useCases: ['Mid-page conversion checkpoints', 'End-of-article engagement hooks', 'Event registration announcements'],
  },
  'cexample:cta-banner-compact': {
    headline: 'Compact CTA banner · inline nudge',
    body: 'Reduces vertical padding to a minimum, allowing the CTA to sit inline with surrounding content without interrupting the reading flow.',
    useCases: ['In-article micro-CTAs', 'Sidebar or panel nudges', 'Cookie or notification banners'],
  },

  // wizard entry (hero-minimal variants)
  'cexample:hero-minimal-default': {
    headline: 'Minimal hero · clean above-the-fold',
    body: 'Prioritises headline typography and a single primary action. Zero decorative clutter ensures the value proposition is the first thing visitors read.',
    useCases: ['SaaS landing pages', 'Product launch announcements', 'Portfolio home screens'],
  },
  'cexample:hero-minimal-dark': {
    headline: 'Dark-mode hero · high-contrast landing',
    body: 'Inverts to a near-black background to convey sophistication and premium positioning. Works especially well for developer or security-focused products.',
    useCases: ['Developer tools homepages', 'Cybersecurity products', 'Premium subscription offers'],
  },
  'cexample:hero-minimal-split-layout': {
    headline: 'Split-layout hero · text + visual columns',
    body: 'Divides the hero into two equal columns — copy on the left, a graphic or screenshot on the right — for a balanced, editorial feel.',
    useCases: ['App screenshots showcases', 'Feature announcement pages', 'Comparison landing pages'],
  },

  // gallery entry (feature-grid variants)
  'cexample:feature-grid-default': {
    headline: 'Feature grid · 3-column benefit showcase',
    body: 'Lays out three equally weighted feature tiles in a row. The symmetric grid creates an impression of breadth and completeness.',
    useCases: ['Marketing sites feature sections', 'Pricing-page benefit tables', 'Onboarding checklists'],
  },
  'cexample:feature-grid-two-column': {
    headline: 'Two-column feature grid · wider content layout',
    body: 'Reduces the column count to two for features that benefit from longer descriptive copy. Each tile gains more horizontal space.',
    useCases: ['Detailed capability comparisons', 'Enterprise product pages', 'Documentation feature overviews'],
  },
  'cexample:feature-grid-with-icons': {
    headline: 'Icon-accented feature grid · visual hierarchy',
    body: 'Adds a primary-coloured icon above each feature text to create a memorable visual anchor. Scanning the icons alone communicates the gist.',
    useCases: ['Consumer app marketing pages', 'Mobile app landing pages', 'Help centre category overviews'],
  },

  // ds-axis entry (card-product with different dsId)
  'cexample:card-product-ds-shadcn': {
    headline: 'Product card · shadcn/ui token set',
    body: 'Uses shadcn/ui semantic design tokens (primary, muted, card, border) so the card adapts automatically to light and dark themes without overrides.',
    useCases: ['shadcn-first design systems', 'Coogo-native apps', 'Theme-toggleable storefronts'],
  },
  'cexample:card-product-ds-tailwind-ui': {
    headline: 'Product card · Tailwind UI token set',
    body: 'Replaces shadcn semantic tokens with Tailwind UI colour scales and spacing utilities. A drop-in option for teams already invested in Tailwind UI components.',
    useCases: ['Tailwind UI design systems', 'Custom Tailwind configs', 'Multi-brand token overrides'],
  },
  'cexample:card-product-ds-radix': {
    headline: 'Product card · Radix primitives token set',
    body: 'Targets Radix UI\'s CSS variable palette, enabling headless-primitive styling patterns. Useful when the surrounding app relies on Radix for accessible interactive components.',
    useCases: ['Radix-first accessibility-focussed apps', 'Custom headless UI stacks', 'Cross-framework design-token bridging'],
  },
}
